
import { Invoice } from '@/types/payments';
import { useSortableTable } from '@/hooks/useSortableTable';
import { useInvoiceState } from '@/hooks/useInvoiceState';
import { useInvoiceDialogs } from '@/hooks/useInvoiceDialogs';
import { useInvoiceActions } from '@/hooks/useInvoiceActions';
import { useInvoiceBulkActions } from '@/hooks/useInvoiceBulkActions';
import { useInvoicePaymentProcessing } from '@/hooks/useInvoicePaymentProcessing';

export function useInvoiceManagement(
  initialInvoices: Invoice[], 
  defaultSortKey: keyof Invoice | null = null,
  defaultSortDirection: 'asc' | 'desc' | 'none' = 'none'
) {
  // Use smaller, focused hooks
  const {
    invoices,
    setInvoices,
    selectedInvoices,
    toggleSelectInvoice,
    toggleSelectAll
  } = useInvoiceState(initialInvoices);

  const {
    paymentDialogOpen,
    setPaymentDialogOpen,
    cancelDialogOpen,
    setCancelDialogOpen,
    actionInvoiceId,
    setActionInvoiceId
  } = useInvoiceDialogs();

  const {
    handleRowClick,
    handleSinglePay,
    handleSingleCancel,
    handleSingleExport,
    handleDownloadInvoice,
    handleEditPayment,
    handleEditVendor,
    handleEditInvoice
  } = useInvoiceActions(invoices, setInvoices);

  const {
    handleBulkPayment,
    handleBulkCancel,
    handleBulkExport
  } = useInvoiceBulkActions(
    invoices, 
    selectedInvoices, 
    setPaymentDialogOpen, 
    setCancelDialogOpen
  );

  const {
    processPayment,
    processCancellation
  } = useInvoicePaymentProcessing(
    invoices, 
    setInvoices, 
    setPaymentDialogOpen, 
    setCancelDialogOpen, 
    actionInvoiceId, 
    selectedInvoices
  );

  // Use the sortable table hook with default sorting
  const { sortConfig, handleSort, renderSortIcon } = useSortableTable<Invoice>(
    defaultSortKey,
    defaultSortDirection
  );

  return {
    invoices,
    setInvoices,
    selectedInvoices,
    sortConfig,
    paymentDialogOpen,
    setPaymentDialogOpen,
    cancelDialogOpen,
    setCancelDialogOpen,
    actionInvoiceId,
    toggleSelectInvoice,
    toggleSelectAll,
    handleSort,
    renderSortIcon,
    handleRowClick,
    handleSinglePay,
    handleSingleCancel,
    handleSingleExport,
    handleDownloadInvoice,
    handleEditPayment,
    handleEditVendor,
    handleEditInvoice,
    processPayment,
    processCancellation,
    handleBulkPayment,
    handleBulkCancel,
    handleBulkExport
  };
}
